import React from 'react';
import classNames from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Box, IconButton } from '@material-ui/core';
import { fade as alpha } from '@material-ui/core/styles/colorManipulator';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    Delete as DeleteIcon,
} from '@material-ui/icons';
import AdditionalAmount from './AdditionalAmount';
import {
    addPerk,
    removePerk,
    selectPerkLineItems,
    selectCartTotal,
    removePerkLineItem,
    updatePerkLineItemAmounts,
    selectPerkAmountProperty,
    selectShowCartValidationErrors,
    selectValidationErrors,
    selectPerksPaymentOptionType,
    selectPerksDuration,
} from '../../features/cart/cartSlice';
import { currencySign } from 'common/helpers';
import {
    selectCartTotalWithTip,
    selectDonationCurrency,
    selectSelectedTipPercent,
    selectTipAmount,
    selectTippingPlatformText,
} from '../../features/checkout/checkoutSlice';
import {
    selectDefaultPage,
    selectIsAdditionalAmountEnabled,
    selectPageName,
} from '../../features/campaign/campaignSlice';
import { changeColorAlpha } from 'client/helpers/colorUtils';
import { PERKS_PAYMENT_OPTIONS } from 'common/constants/perks';
import { formatDonationAmount } from 'client/helpers/formatDonationAmount';
import { handleIntegerInput } from 'client/helpers/handleIntegerInput';
import { createPageOptionsSelector } from 'client/components/GivingOptionsEditor/givingSelectors';
import { minDonationAmount } from 'common/helpers/donation';

const useStyles = makeStyles(
    ({ breakpoints, palette, spacing, typography }) => ({
        dialogTitle: {
            padding: spacing(3, 3, 1, 3),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        title: {
            fontSize: typography.pxToRem(24),
            fontWeight: 600,
        },
        closeButton: {
            position: 'absolute',
            right: spacing(1),
            top: spacing(1),
        },
        cartItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: spacing(2, 0),
            borderBottom: `1px solid ${palette.divider}`,
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                flexDirection: 'column',
                alignItems: 'start',
            },
        },
        perkInfo: {
            display: 'flex',
        },
        itemName: {
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                display: '-webkit-box',
                maxWidth: '23rem',
                '-webkit-line-clamp': '2',
                '-webkit-box-orient': 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        },
        imagePlaceholder: {
            margin: 0,
            marginInlineEnd: spacing(1),
            '& img': {
                width: 60,
                height: 60,
                borderRadius: spacing(1),
                border: `2px solid ${palette.primary.main}`,
            },
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                '& img': {
                    width: 40,
                    height: 40,
                },
            },
        },
        noLogo: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginInlineEnd: spacing(1),
            width: 60,
            height: 60,
            borderRadius: spacing(1),
            opacity: 0.5,
            background: palette.background.primaryLight,
            border: `2px solid ${palette.background.accent}`,
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                width: 40,
                height: 40,
            },
        },
        itemDetails: {
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginInlineEnd: spacing(2),
        },
        itemPrice: {
            fontSize: typography.pxToRem(16),
            color: palette.text.secondary,
        },
        cartAmountInput: {
            display: 'flex',
            alignItems: 'center',
            '& input': {
                fontSize: '1rem',
                width: 90,
                textAlign: 'end',
                paddingLeft: spacing(1),
                paddingRight: spacing(1),
                borderRadius: 5,
                border: `1px solid ${palette.divider}`,
                height: 26,
            },
            '& .currencySign': {
                marginInlineEnd: spacing(1),
            },
            '& .error': {
                background: changeColorAlpha(palette.error.light, 0.1),
                border: `1px solid ${palette.error.main}`,
                '&:focus-visible': {
                    outlineColor: palette.error.dark,
                },
            },
        },
        additionalAmountInput: {
            alignItems: 'center',
            display: 'flex',
            '& input': {
                width: 72,
            },
        },
        newAdditionalAmount: {
            display: 'flex',
            alignItems: 'start',
            flexDirection: 'column',
            width: '100%',
            '& .addDonationButton': {
                fontFamily: typography.fontFamily,
                textWrap: 'nowrap',
                color: 'white',
            },
            '& .inputWithCurrency': {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
            },
            '& .inputWrapper': {
                marginTop: spacing(0.5),
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
            },
            '& .inputWithSuffix': {
                border: `1px solid ${palette.divider}`,
                borderRadius: '5px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                marginInlineEnd: spacing(1),
                direction: 'ltr',
                'html[dir=rtl] &': {
                    marginInlineStart: spacing(1),
                    marginInlineEnd: spacing(0),
                },
            },
            '& input': {
                width: '100%',
                height: '34px',
                border: 'none',
                paddingInlineEnd: spacing(0),
                paddingInlineStart: spacing(1),
                '&:focus-visible': {
                    outline: 'none',
                },
                '&::-webkit-input-placeholder': {
                    color: alpha(palette.text.primary, 0.3),
                    textAlign: 'start',
                },
                '&::-moz-placeholder': {
                    color: alpha(palette.text.primary, 0.3),
                    textAlign: 'start',
                },
                '&:-moz-placeholder': {
                    color: alpha(palette.text.primary, 0.3),
                    textAlign: 'start',
                },
                '&:-ms-input-placeholder': {
                    color: alpha(palette.text.primary, 0.3),
                    textAlign: 'start',
                },
            },
        },
        paddings: {
            padding: `${spacing(1)}px ${spacing(3)}px ${spacing(1)}px ${spacing(
                3,
            )}px`,
        },
        smallPaddings: {
            padding: `${spacing(0.5)}px ${spacing(1.5)}px ${spacing(
                0.5,
            )}px ${spacing(1.5)}px`,
        },
        itemActions: {
            display: 'flex',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                width: '100%',
                justifyContent: 'end',
            },
        },
        quantityControls: {
            display: 'flex',
            alignItems: 'center',
            marginRight: spacing(2),
        },
        quantityButton: {
            borderRadius: 5,
            minWidth: 'auto',
            padding: spacing(0.5),
            border: `1px solid ${palette.divider}`,
            '&:disabled': {
                cursor: 'no-drop',
                pointerEvents: 'unset',
            },
        },
        quantityInput: {
            width: 30,
            textAlign: 'center',
            border: 'none',
            fontSize: typography.pxToRem(16),
        },
        deleteButton: {
            color: palette.text.secondary,
        },
        totalsSection: {
            marginTop: spacing(2),
            marginBottom: spacing(4),
        },
        subtotalRow: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: spacing(1),
        },
        totalRow: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: spacing(1),
            fontWeight: 'bold',
        },
        recurringBreakdown: {
            display: 'flex',
            justifyContent: 'end',
        },
        suffix: {
            fontFamily: typography.fontFamily,
            direction: 'ltr',
            flexShrink: 0,
            paddingInlineEnd: spacing(2),

            '@media (max-width: 600px)': {
                top: '50%',
            },

            '@media (max-width: 400px)': {
                paddingInlineEnd: spacing(1),
            },
        },
    }),
);

export const ShoppingCartList = ({
    addPerk,
    defaultAmountProp = 'amount',
    defaultPage,
    enableInputs = false,
    perkLineItems = [],
    perksDuration,
    perksPaymentOption,
    removePerk,
    removePerkLineItem,
    selectedCurrency,
    selectedPage,
    showValidationErrors,
    tipAmount,
    tipPercent,
    tippingPlatformText,
    total,
    totalWithTip,
    updatePerkAmount,
    validationErrors,
    enableAdditionalAmount,
}) => {
    const classes = useStyles();
    const isRecurring = perksPaymentOption === PERKS_PAYMENT_OPTIONS.RECURRING;

    const handlePerkAmountChange = (perkId, value) => {
        updatePerkAmount(perkId, value);
    };
    const getPageOption = createPageOptionsSelector(selectedPage, defaultPage);

    return (
        <>
            {perkLineItems.map(item => {
                const itemName = getPageOption(item, 'name');
                const displayAmount = item[defaultAmountProp];
                const primaryCurrencyAmountProp =
                    defaultAmountProp === 'amount'
                        ? 'primaryCurrencyAmount'
                        : 'primaryCurrencyRecurringAmount';
                const isUnspecifiedAmount = !item[primaryCurrencyAmountProp];
                const isInvalidAmount =
                    showValidationErrors &&
                    get(validationErrors, item.perkId, []).filter(
                        ({ error }) => error === 'invalidAmount',
                    ).length > 0;
                const showPrice =
                    !isUnspecifiedAmount || (!enableInputs && displayAmount);

                return (
                    <Box key={item.perkId} className={classes.cartItem}>
                        <Box className={classes.perkInfo}>
                            {item?.logo && (
                                <figure className={classes.imagePlaceholder}>
                                    <img
                                        style={{ borderColor: item.color }}
                                        src={item.logo}
                                    />
                                </figure>
                            )}
                            {!item.logo && (
                                <div className={classes.noLogo}>
                                    <Typography>
                                        <FormattedMessage
                                            id="shoppingCart.imageNotAvailable"
                                            defaultMessage="n/a"
                                        />
                                    </Typography>
                                </div>
                            )}
                            <Box className={classes.itemDetails}>
                                <Box>
                                    <Typography
                                        className={classes.itemName}
                                        variant={'h6'}
                                    >
                                        {itemName}
                                    </Typography>
                                    {showPrice && (
                                        <Typography
                                            className={classes.itemPrice}
                                        >
                                            {formatDonationAmount(
                                                displayAmount,
                                                selectedCurrency,
                                                {
                                                    isRecurring,
                                                    recurringDuration:
                                                        perksDuration,
                                                },
                                            )}{' '}
                                            <FormattedMessage
                                                id="shoppingCart.eachLabel"
                                                defaultMessage="each"
                                            />
                                        </Typography>
                                    )}
                                    {!showPrice && enableInputs && (
                                        <Typography
                                            className={classes.itemPrice}
                                        >
                                            <FormattedMessage
                                                id="shoppingCart.enterYourAmount"
                                                defaultMessage="Enter your amount"
                                            />
                                        </Typography>
                                    )}
                                    {!showPrice && !enableInputs && (
                                        <Typography
                                            className={classes.itemPrice}
                                        >
                                            <FormattedMessage
                                                id="shoppingCart.enterYourAmountDuringCheckout"
                                                defaultMessage="Enter your amount during checkout"
                                            />
                                        </Typography>
                                    )}
                                    {enableInputs && isInvalidAmount && (
                                        <Typography
                                            color="error"
                                            variant="caption"
                                        >
                                            <FormattedMessage
                                                id="DonationForm.minimumAmount"
                                                defaultMessage="Required minimum donation amount is {minDonation}"
                                                values={{
                                                    minDonation:
                                                        minDonationAmount(
                                                            selectedCurrency,
                                                        ),
                                                }}
                                            />
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        <Box className={classes.itemActions}>
                            {isUnspecifiedAmount && enableInputs && (
                                <Box
                                    className={classNames(
                                        classes.cartAmountInput,
                                        classes.paddings,
                                    )}
                                >
                                    <span className="currencySign">
                                        {currencySign(selectedCurrency)}
                                    </span>
                                    <input
                                        value={
                                            item[defaultAmountProp]
                                                ? item[
                                                      defaultAmountProp
                                                  ].toLocaleString('en-EN')
                                                : ''
                                        }
                                        className={classNames({
                                            error: isInvalidAmount,
                                        })}
                                        type="text"
                                        inputMode="numeric"
                                        onChange={event =>
                                            handleIntegerInput(value =>
                                                handlePerkAmountChange(
                                                    item.perkId,
                                                    value,
                                                ),
                                            )(event)
                                        }
                                    />
                                </Box>
                            )}
                            <Box className={classes.quantityControls}>
                                <IconButton
                                    className={classes.quantityButton}
                                    onClick={() => removePerk(item.perkId)}
                                >
                                    <RemoveIcon fontSize="small" />
                                </IconButton>
                                <input
                                    className={classes.quantityInput}
                                    value={item.quantity}
                                    readOnly
                                />
                                <IconButton
                                    disabled={
                                        item.limit &&
                                        item.quantity === item.limit
                                    }
                                    className={classes.quantityButton}
                                    onClick={() => addPerk(item)}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <IconButton
                                className={classes.deleteButton}
                                onClick={() => removePerkLineItem(item.perkId)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    </Box>
                );
            })}
            {enableAdditionalAmount && (
                <AdditionalAmount
                    enableInputs={enableInputs}
                    classes={classes}
                />
            )}
            <Box className={classes.totalsSection}>
                <Box className={classes.totalRow}>
                    <Typography variant="subtitle1">
                        <FormattedMessage
                            id="shoppingCart.subTotalLabel"
                            defaultMessage="Subtotal:"
                        />
                    </Typography>
                    <Typography variant="subtitle1">
                        {formatDonationAmount(total, selectedCurrency)}
                    </Typography>
                </Box>
                {tipPercent > 0 && (
                    <Box className={classes.totalRow}>
                        <Typography variant="subtitle1">
                            {tippingPlatformText}
                            <FormattedMessage
                                id="shoppingCart.tip"
                                defaultMessage=" ({tipPercent}%)"
                                values={{ tipPercent }}
                            />
                        </Typography>
                        <Typography variant="subtitle1">
                            {formatDonationAmount(tipAmount, selectedCurrency)}
                        </Typography>
                    </Box>
                )}
                {isRecurring && (
                    <Box className={classes.totalRow}>
                        <Typography variant="subtitle1">
                            <FormattedMessage
                                id="shoppingCart.duration"
                                defaultMessage="Duration:"
                            />
                        </Typography>
                        <Typography variant="subtitle1">
                            <FormattedMessage
                                id="shoppingCart.durationValue"
                                defaultMessage="{duration} months"
                                values={{ duration: perksDuration }}
                            />
                        </Typography>
                    </Box>
                )}
                <Box className={classes.totalRow}>
                    <Typography variant="h5">
                        <FormattedMessage
                            id="shoppingCart.totalAmount"
                            defaultMessage="Total Donation:"
                        />
                    </Typography>
                    <Typography variant="h5">
                        {formatDonationAmount(totalWithTip, selectedCurrency)}
                    </Typography>
                </Box>
                {isRecurring && (
                    <Box className={classes.recurringBreakdown}>
                        <Typography variant="caption">
                            <FormattedMessage
                                id="DonationForm.totalBreakdown"
                                values={{
                                    currencySign:
                                        currencySign(selectedCurrency),
                                    monthlyAmount: total,
                                    months: perksDuration,
                                }}
                            />
                        </Typography>
                    </Box>
                )}
            </Box>
        </>
    );
};

ShoppingCartList.propTypes = {
    addPerk: PropTypes.func.isRequired,
    defaultAmountProp: PropTypes.string,
    defaultPage: PropTypes.string,
    enableInputs: PropTypes.bool,
    enableAdditionalAmount: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool.isRequired,
    perkLineItems: PropTypes.array.isRequired,
    perksDuration: PropTypes.number,
    perksPaymentOption: PropTypes.string,
    proceedToCheckout: PropTypes.func.isRequired,
    removePerk: PropTypes.func.isRequired,
    removePerkLineItem: PropTypes.func.isRequired,
    selectedCurrency: PropTypes.string,
    selectedPage: PropTypes.string,
    showValidationErrors: PropTypes.bool,
    tipAmount: PropTypes.number,
    tipPercent: PropTypes.number,
    tippingPlatformText: PropTypes.string,
    total: PropTypes.number.isRequired,
    totalWithTip: PropTypes.number,
    updatePerkAmount: PropTypes.func,
    validationErrors: PropTypes.object,
};

const mapStateToProps = state => ({
    defaultAmountProp: selectPerkAmountProperty(state),
    enableAdditionalAmount: selectIsAdditionalAmountEnabled(state),
    perkLineItems: selectPerkLineItems(state),
    perksDuration: selectPerksDuration(state),
    perksPaymentOption: selectPerksPaymentOptionType(state),
    selectedCurrency: selectDonationCurrency(state),
    selectedPage: selectPageName(state),
    defaultPage: selectDefaultPage(state),
    showValidationErrors: selectShowCartValidationErrors(state),
    tipAmount: selectTipAmount(state),
    tipPercent: selectSelectedTipPercent(state),
    tippingPlatformText: selectTippingPlatformText(state),
    total: selectCartTotal(state),
    totalWithTip: selectCartTotalWithTip(state),
    validationErrors: selectValidationErrors(state),
});

const mapDispatchToProps = {
    addPerk,
    removePerk,
    removePerkLineItem,
    updatePerkAmount: (perkId, amount) =>
        updatePerkLineItemAmounts([
            { perkId, amount, recurringAmount: amount },
        ]),
};

export default connect(mapStateToProps, mapDispatchToProps)(ShoppingCartList);
